import {
  clearCursorAgentRunPending,
  loadCursorAgentRunPending,
  loadStoredCursorAgentId,
  markCursorAgentRunPending,
  resumeCursorAgentMessage,
  saveStoredCursorAgentId,
  sendCursorAgentMessage,
  type CursorAgentProgress,
} from '../services/cursorAgentClient';
import { copyTextToClipboard } from '../utils/formatListClipboard';
import { flashCopyOnButton } from '../utils/copyFeedback';
import {
  clearCursorAgentHistory,
  loadCursorAgentHistory,
  saveCursorAgentHistory,
  type CursorAgentMessage,
} from '../utils/cursorAgentHistory';
import { loadCursorAgentPreferences } from '../utils/cursorAgentPreferences';
import { formatRunDurationMs } from '../utils/cursorAgentUsageInfo';
import { shouldResumeAgentChat } from '../utils/cursorAgentResume';
import { cursorAgentConversationUrl } from '../utils/cursorAgentUrls';
import {
  cursorAgentRunErrorHandling,
  formatCursorFetchErrorBrief,
} from '../utils/cursorFetchError';

const LAST_RUN_DURATION_KEY = 'cursorAgent.lastRunDurationMs';

export type CursorAgentViewDeps = {
  onQuickGrocery?: () => void | Promise<void>;
  onQuickTodo?: () => void | Promise<void>;
};

/** Chat with a Cursor cloud agent (API key in Tools → Config; same-origin CORS proxy). */
export class CursorAgentView {
  private container: HTMLElement | null = null;
  private agentId: string | null = loadStoredCursorAgentId();
  private agentUrl: string | null = null;
  private messages: CursorAgentMessage[] = loadCursorAgentHistory();
  private busy = false;
  private lastRunDurationMs: number | null = this.loadLastRunDuration();

  render(container: HTMLElement, deps: CursorAgentViewDeps = {}): void {
    this.container = container;
    this.messages = loadCursorAgentHistory();
    this.agentId = loadStoredCursorAgentId();
    container.innerHTML = `
      <div class="chat-view cursor-agent-view">
        <nav class="cursor-agent-quick-nav" aria-label="Quick add">
          <button type="button" class="cursor-agent-quick-btn" id="cursor-agent-quick-grocery">🛒 Grocery</button>
          <button type="button" class="cursor-agent-quick-btn" id="cursor-agent-quick-todo">✓ Todo</button>
        </nav>
        <header class="chat-header cursor-agent-header">
          <div class="cursor-agent-header-row">
            <h2 class="file-title">AI Agent</h2>
            <div class="cursor-agent-header-actions">
              <a
                href="#"
                id="cursor-agent-convo-link"
                class="btn-icon cursor-agent-convo-link hidden"
                target="_blank"
                rel="noopener noreferrer"
                title="Open this conversation in Cursor"
                aria-label="Open this conversation in Cursor"
              >🔗</a>
              <button type="button" class="btn-ghost-dark btn-sm cursor-agent-new-convo" id="cursor-agent-new-convo">New conversation</button>
            </div>
          </div>
        </header>
        <div class="chat-messages" id="cursor-agent-messages" aria-live="polite">
          <p class="chat-empty-hint" id="cursor-agent-empty">Ask anything — the agent may take a minute to respond.</p>
        </div>
        <p class="chat-status cursor-agent-status" id="cursor-agent-status" role="status"></p>
        <form class="chat-composer" id="cursor-agent-form">
          <textarea
            id="cursor-agent-input"
            class="chat-input"
            rows="4"
            placeholder="Message the AI agent…"
            aria-describedby="cursor-agent-empty"
          ></textarea>
          <button type="submit" class="btn-primary chat-send" id="cursor-agent-send">Send</button>
        </form>
      </div>`;

    this.syncConversationLink();
    this.paintMessages();

    container.querySelector('#cursor-agent-new-convo')?.addEventListener('click', () => {
      this.startNewConversation();
    });

    container.querySelector('#cursor-agent-quick-grocery')?.addEventListener('click', () => {
      void deps.onQuickGrocery?.();
    });
    container.querySelector('#cursor-agent-quick-todo')?.addEventListener('click', () => {
      void deps.onQuickTodo?.();
    });

    const form = container.querySelector<HTMLFormElement>('#cursor-agent-form')!;
    const input = container.querySelector<HTMLTextAreaElement>('#cursor-agent-input')!;
    form.addEventListener('submit', e => {
      e.preventDefault();
      if (this.busy) return;
      void this.send(input.value.trim());
      input.value = '';
    });
    input.addEventListener('keydown', e => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (this.busy) return;
        form.requestSubmit();
      }
    });

    this.syncComposer();
    void this.tryResumePendingRun();
  }

  private loadLastRunDuration(): number | null {
    try {
      const raw = localStorage.getItem(LAST_RUN_DURATION_KEY);
      if (!raw) return null;
      const n = Number(raw);
      return Number.isFinite(n) && n >= 0 ? n : null;
    } catch {
      return null;
    }
  }

  private saveLastRunDuration(ms: number | undefined): void {
    if (ms == null || !Number.isFinite(ms) || ms < 0) return;
    this.lastRunDurationMs = ms;
    try {
      localStorage.setItem(LAST_RUN_DURATION_KEY, String(ms));
    } catch {
      /* ignore */
    }
  }

  private syncConversationLink(): void {
    const anchor = this.container?.querySelector<HTMLAnchorElement>('#cursor-agent-convo-link');
    if (!anchor) return;
    const url =
      this.agentUrl?.trim() ||
      (this.agentId ? cursorAgentConversationUrl(this.agentId) : '');
    if (!url) {
      anchor.classList.add('hidden');
      anchor.removeAttribute('href');
      return;
    }
    anchor.classList.remove('hidden');
    anchor.href = url;
  }

  private startNewConversation(): void {
    this.agentId = null;
    this.agentUrl = null;
    this.busy = false;
    this.messages = [];
    saveStoredCursorAgentId(null);
    clearCursorAgentRunPending();
    clearCursorAgentHistory();
    this.syncConversationLink();
    this.syncComposer();
    const messages = this.container?.querySelector<HTMLElement>('#cursor-agent-messages');
    if (messages) {
      messages.innerHTML =
        '<p class="chat-empty-hint" id="cursor-agent-empty">New conversation — ask anything.</p>';
    }
    this.setStatus('Started a new conversation.');
  }

  private persistMessages(): void {
    saveCursorAgentHistory(this.messages);
  }

  private paintMessages(): void {
    const messages = this.container?.querySelector<HTMLElement>('#cursor-agent-messages');
    if (!messages) return;
    messages.replaceChildren();
    if (this.messages.length === 0) {
      const hint = document.createElement('p');
      hint.className = 'chat-empty-hint';
      hint.id = 'cursor-agent-empty';
      hint.textContent = 'Ask anything — the agent may take a minute to respond.';
      messages.appendChild(hint);
      return;
    }
    for (const msg of this.messages) {
      this.appendBubbleTo(messages, msg.role, msg.text);
    }
    messages.scrollTop = messages.scrollHeight;
  }

  private setStatus(text: string): void {
    const el = this.container?.querySelector<HTMLElement>('#cursor-agent-status');
    if (el) el.textContent = text;
  }

  private formatProgress(progress: CursorAgentProgress): string {
    if (progress.phase === 'poll' && progress.status) {
      const n = progress.poll ?? 0;
      const secs = n * 2;
      return `Agent ${progress.status}… (${secs}s)`;
    }
    if (progress.phase === 'create') return 'Starting agent run…';
    if (progress.phase === 'fetch-agent') return 'Loading agent…';
    return 'Waiting for AI agent…';
  }

  private attachCopyButton(wrap: HTMLElement, text: string): void {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'chat-bubble-copy btn-icon';
    btn.title = 'Copy message';
    btn.setAttribute('aria-label', 'Copy message');
    btn.textContent = '📋';
    btn.addEventListener('click', () => {
      void copyTextToClipboard(text).then(() => flashCopyOnButton(btn));
    });
    wrap.appendChild(btn);
  }

  private appendBubbleTo(
    messages: HTMLElement,
    role: 'user' | 'assistant',
    text: string,
    streaming = false
  ): HTMLElement {
    messages.querySelector('#cursor-agent-empty')?.remove();
    const wrap = document.createElement('div');
    wrap.className = `chat-bubble-wrap chat-bubble-wrap-${role}`;
    const bubble = document.createElement('div');
    bubble.className = `chat-bubble chat-bubble-${role}${streaming ? ' chat-bubble-streaming' : ''}`;
    bubble.textContent = text;
    wrap.appendChild(bubble);
    if (!streaming && text.trim() && !text.startsWith('Thinking…')) {
      this.attachCopyButton(wrap, text);
    }
    messages.appendChild(wrap);
    messages.scrollTop = messages.scrollHeight;
    return bubble;
  }

  private appendBubble(role: 'user' | 'assistant', text: string, streaming = false): HTMLElement | null {
    const messages = this.container?.querySelector<HTMLElement>('#cursor-agent-messages');
    if (!messages) return null;
    return this.appendBubbleTo(messages, role, text, streaming);
  }

  private refreshBubbleCopy(wrap: HTMLElement | null, text: string): void {
    if (!wrap) return;
    const parent = wrap.parentElement;
    if (!parent?.classList.contains('chat-bubble-wrap')) return;
    const existing = parent.querySelector('.chat-bubble-copy');
    existing?.remove();
    if (text.trim() && !text.startsWith('Thinking…') && !text.startsWith('Error:')) {
      this.attachCopyButton(parent, text);
    }
  }

  private pushMessage(role: 'user' | 'assistant', text: string): void {
    this.messages.push({ role, text });
    this.persistMessages();
  }

  private keepThinkingBubble(bubble: HTMLElement | null): void {
    if (!bubble) return;
    bubble.textContent = 'Thinking…';
    bubble.classList.add('chat-bubble-streaming');
  }

  private handleRunError(
    err: unknown,
    pendingBubble: HTMLElement | null
  ): { keepBusy: boolean; keepPending: boolean } {
    const handling = cursorAgentRunErrorHandling(err);
    if (handling.keepThinking) {
      this.keepThinkingBubble(pendingBubble);
    } else if (pendingBubble) {
      pendingBubble.textContent = 'Could not finish — see status below.';
      pendingBubble.classList.remove('chat-bubble-streaming');
    }
    this.setStatus(formatCursorFetchErrorBrief(err));
    return { keepBusy: handling.keepBusy, keepPending: handling.keepPending };
  }

  private findOrCreateThinkingBubble(): HTMLElement | null {
    const messages = this.container?.querySelector<HTMLElement>('#cursor-agent-messages');
    if (!messages) return null;
    const wraps = messages.querySelectorAll<HTMLElement>('.chat-bubble-wrap-assistant');
    const lastWrap = wraps[wraps.length - 1];
    const lastBubble = lastWrap?.querySelector<HTMLElement>('.chat-bubble');
    if (
      lastBubble?.classList.contains('chat-bubble-streaming') ||
      lastBubble?.textContent?.startsWith('Thinking…')
    ) {
      return lastBubble;
    }
    const lastMsg = this.messages[this.messages.length - 1];
    if (lastMsg?.role === 'user') {
      return this.appendBubble('assistant', 'Thinking…', true);
    }
    return null;
  }

  private applyAgentResult(result: {
    agentId: string;
    reply: string;
    agentUrl?: string;
    durationMs?: number;
    status?: string;
  }): void {
    this.agentId = result.agentId;
    if (result.agentUrl) this.agentUrl = result.agentUrl;
    saveStoredCursorAgentId(result.agentId);
    this.syncConversationLink();
    if (result.durationMs != null) this.saveLastRunDuration(result.durationMs);
    this.pushMessage('assistant', result.reply);
    this.paintMessages();
    const dur = formatRunDurationMs(result.durationMs);
    const statusNote = [
      result.status ? `Done (${result.status}).` : '',
      dur ? `Run took ${dur}.` : '',
    ]
      .filter(Boolean)
      .join(' ');
    this.setStatus(statusNote);
  }

  private async tryResumePendingRun(): Promise<void> {
    if (this.busy) return;
    if (!loadCursorAgentPreferences().apiKey.trim()) return;

    const pendingStored = loadCursorAgentRunPending();
    const agentId = (this.agentId ?? pendingStored?.agentId)?.trim();
    if (!agentId) {
      clearCursorAgentRunPending();
      return;
    }
    this.agentId = agentId;

    const needsResume =
      shouldResumeAgentChat(this.messages) ||
      (pendingStored?.agentId === agentId && this.messages.length > 0);
    if (!needsResume) {
      clearCursorAgentRunPending();
      return;
    }

    this.busy = true;
    this.syncComposer();
    const pendingBubble = this.findOrCreateThinkingBubble();
    this.setStatus('Resuming agent…');

    let retainBusy = false;
    let retainPending = false;
    try {
      const result = await resumeCursorAgentMessage(agentId, progress => {
        this.setStatus(this.formatProgress(progress));
        if (progress.agentId) {
          markCursorAgentRunPending(progress.agentId, progress.runId);
        }
      });
      if (!result) {
        this.setStatus('No active agent run found.');
        if (pendingBubble) {
          pendingBubble.textContent = 'No response yet — send another message or check Cursor.';
          pendingBubble.classList.remove('chat-bubble-streaming');
        }
        return;
      }
      this.applyAgentResult(result);
    } catch (err) {
      const handling = this.handleRunError(err, pendingBubble);
      retainBusy = handling.keepBusy;
      retainPending = handling.keepPending;
    } finally {
      if (!retainBusy) this.busy = false;
      if (!retainPending) clearCursorAgentRunPending();
      this.syncComposer();
    }
  }

  private async send(text: string): Promise<void> {
    if (!text || this.busy) return;
    if (!loadCursorAgentPreferences().apiKey.trim()) {
      this.setStatus('Add your Cursor API key in Tools → Config.');
      return;
    }
    this.busy = true;
    this.syncComposer();
    if (this.agentId) markCursorAgentRunPending(this.agentId);

    this.pushMessage('user', text);
    this.appendBubble('user', text);
    const pending = this.appendBubble('assistant', 'Thinking…', true);
    this.setStatus('Waiting for AI agent…');

    let retainBusy = false;
    let retainPending = false;
    try {
      const result = await sendCursorAgentMessage(text, this.agentId, progress => {
        this.setStatus(this.formatProgress(progress));
        if (progress.agentId) {
          markCursorAgentRunPending(progress.agentId, progress.runId);
        }
      });
      this.applyAgentResult(result);
    } catch (err) {
      const handling = this.handleRunError(err, pending);
      retainBusy = handling.keepBusy;
      retainPending = handling.keepPending;
    } finally {
      if (!retainBusy) this.busy = false;
      if (!retainPending) clearCursorAgentRunPending();
      this.syncComposer();
    }
  }

  private syncComposer(): void {
    const send = this.container?.querySelector<HTMLButtonElement>('#cursor-agent-send');
    const input = this.container?.querySelector<HTMLTextAreaElement>('#cursor-agent-input');
    const hasKey = Boolean(loadCursorAgentPreferences().apiKey.trim());
    if (send) send.disabled = this.busy || !hasKey;
    if (input) input.disabled = this.busy;
  }
}
